import React from 'react';
import { useTranslationWithNamespace } from '@gov-nx/core/service';
import type { PageCode } from '@gov-nx/module/page';
import {
	formatZakladatelName,
	useEPeticeSeznamContext,
} from '@gov-nx/module/page';
import {
	Chip,
	ChipNav,
	ContentLayout,
	ErrorStatusBanner,
	InfoBanner,
	LayoutGap,
	Pagination,
	Space,
	Tile,
	Tiles,
} from '@gov-nx/ui/web';
import { toDateReadable } from '@gov-nx/utils/common';
import { pageRouteResolver, scrollToTop } from '@gov-nx/web';
import { EPetitionChips } from '../components/EPetitionChips';
import { EPetitionFilter } from './EPetitionFilter';
import { EPetitionListSkeleton } from './EPetitionSkeleton';

export interface EPeticeFormProps {
	code: PageCode;
}

export const EPetitionView = ({ code }: EPeticeFormProps) => {
	const { getLocalizeCurried } = useTranslationWithNamespace();
	const ts = getLocalizeCurried(code);
	const { query, data, numberOfPetition, goToPage, filter, badges } =
		useEPeticeSeznamContext();

	if (query.error) {
		return <ErrorStatusBanner code={code} />;
	}
	return (
		<ContentLayout>
			<LayoutGap gap="l">
				<EPetitionFilter code={code} />
				<ChipNav aria-label={ts('filtr.kategorie')}>
					{badges.map((badge) => (
						<li key={badge.name}>
							<Chip
								asChild
								role="tab"
								color="primary"
								size="m"
								type={badge.isActive ? 'solid' : 'outlined'}
								aria-selected={badge.isActive}
								aria-label={`${ts('formular.wcag.filtrovat')} ${badge.name}`}>
								<button
									type="button"
									onClick={() => badge.filterFunction()}>
									{badge.name}
								</button>
							</Chip>
						</li>
					))}
				</ChipNav>

				{!query.isFetching && data.length === 0 && (
					<InfoBanner
						headline={ts('stav.chyba.zadne-epetice')}
						text={ts('stav.chyba.zadne-epetice-k-podpisu')}
					/>
				)}

				{query.isFetching ? (
					<EPetitionListSkeleton />
				) : (
					<Tiles>
						{data.map((petition) => (
							<Tile
								key={petition.id}
								name={`${petition.nazev}`}
								to={pageRouteResolver('epetice.detail', {
									petitionId: petition.id,
								})}
								icon={{ name: 'chevron-right', type: 'components' }}>
								<LayoutGap gap="s">
									<EPetitionChips
										code={code}
										petition={petition}
									/>
									<p className="text-(--text-secondary)!">
										{ts('formular.petice.zakladatel')}
										<Space />
										<strong>{formatZakladatelName(petition.zakladatel)}</strong>
										{ts('formular.petice.adresat')}
										<Space />
										<strong>{petition.adresat?.nazev}</strong>
										<Space />
										{ts('formular.petice.zverejneno')}
										<Space />
										<strong>
											{petition.datumZverejneni &&
												toDateReadable(petition.datumZverejneni)}
										</strong>
										<Space />
									</p>
									<p className="text-(--text-secondary)!">{petition.perex}</p>
								</LayoutGap>
							</Tile>
						))}
					</Tiles>
				)}

				{numberOfPetition > filter.pocet ? (
					<Pagination
						color="primary"
						size="m"
						onPage={(event) => {
							scrollToTop();
							goToPage(event);
						}}
						current={filter.start / filter.pocet + 1}
						pageSize={filter.pocet}
						total={numberOfPetition}
						wcag-label={ts('strankovani')}
					/>
				) : null}
			</LayoutGap>
		</ContentLayout>
	);
};
